import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import dayjs from 'dayjs';

import { useAuth } from '../../context/authContext';
import useExperiments from '../../hooks/useExperiments';

const READ_RIGHT = 'experiment:read';
const MANAGE_RIGHT = 'experiment:manage';

// Minimum sessions per arm before we trust a significance verdict.
const MIN_SESSIONS_FOR_VERDICT = 100;
const CONFIDENCE_THRESHOLD = 0.95;

const fmtPct = x =>
  `${(Number(x || 0) * 100).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;

const fmtSignedPp = x => {
  if (x === null || x === undefined) return '—';
  const v = Number(x) * 100;
  return `${v >= 0 ? '+' : ''}${v.toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} pp`;
};

const fmtSignedRel = x => {
  if (x === null || x === undefined) return '—';
  const v = Number(x) * 100;
  return `${v >= 0 ? '+' : ''}${v.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} %`;
};

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

// Map a variant's stats to a significance verdict chip.
function verdict(variant, controlSessions) {
  if (variant.isControl) return { label: 'Baseline', color: 'default' };
  if (!variant.sessions || !controlSessions) return { label: 'Keine Daten', color: 'default' };
  if (Math.min(variant.sessions, controlSessions) < MIN_SESSIONS_FOR_VERDICT) {
    return { label: 'Mehr Daten nötig', color: 'warning' };
  }
  if (variant.confidence !== null && variant.confidence >= CONFIDENCE_THRESHOLD) {
    return (variant.crUpliftPp || 0) >= 0
      ? { label: 'Signifikant besser', color: 'success' }
      : { label: 'Signifikant schlechter', color: 'error' };
  }
  return { label: 'Kein klarer Unterschied', color: 'default' };
}

function ResultsPanel({ result }) {
  if (!result) return null;
  const variants = result.variants || [];
  const control = variants.find(v => v.isControl) || { sessions: 0 };
  const maxCr = Math.max(0.0001, ...variants.map(v => v.conversionRate || 0));

  // Leading variant: significant positive uplift, highest CR.
  const leader = variants
    .filter(v => !v.isControl)
    .filter(v => verdict(v, control.sessions).color === 'success')
    .sort((a, b) => b.conversionRate - a.conversionRate)[0];

  if (result.totalExposed === 0) {
    return (
      <Alert severity='info' sx={{ mt: 1 }}>
        Noch keine Daten im gewählten Zeitraum. Das Experiment muss aktiv sein und Sessions sammeln —
        sobald Besucher den Warenkorb erreichen, erscheinen hier die Varianten-Metriken.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
        <QueryStatsIcon fontSize='small' color='primary' />
        <Typography variant='body2'>
          <strong>{result.totalExposed.toLocaleString('de-DE')}</strong> exponierte Sessions im
          Zeitraum
        </Typography>
        {leader && (
          <Chip
            size='small'
            color='success'
            icon={<EmojiEventsIcon />}
            label={`Führend: ${leader.name}`}
          />
        )}
      </Stack>

      <TableContainer component={Paper} variant='outlined'>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Variante</TableCell>
              <TableCell align='right'>Sessions</TableCell>
              <TableCell align='right'>Conv.</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Conversion-Rate</TableCell>
              <TableCell align='right'>Δ vs Control</TableCell>
              <TableCell align='right'>Uplift</TableCell>
              <TableCell align='right'>€/Session</TableCell>
              <TableCell align='right'>Konfidenz</TableCell>
              <TableCell>Bewertung</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {variants.map(v => {
              const vd = verdict(v, control.sessions);
              const barColor =
                vd.color === 'success'
                  ? 'success'
                  : vd.color === 'error'
                    ? 'error'
                    : 'primary';
              return (
                <TableRow key={v.key} hover>
                  <TableCell>
                    <Tooltip title={v.description || ''} placement='top' arrow>
                      <Box>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {v.name}
                          {v.isControl ? ' ·' : ''}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {v.key}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align='right'>{v.sessions.toLocaleString('de-DE')}</TableCell>
                  <TableCell align='right'>{v.conversions.toLocaleString('de-DE')}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        {fmtPct(v.conversionRate)}
                      </Typography>
                      <LinearProgress
                        variant='determinate'
                        color={barColor}
                        value={Math.min(100, (v.conversionRate / maxCr) * 100)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell align='right'>{v.isControl ? '—' : fmtSignedPp(v.crUpliftPp)}</TableCell>
                  <TableCell align='right'>{v.isControl ? '—' : fmtSignedRel(v.crUpliftRel)}</TableCell>
                  <TableCell align='right'>{eur.format(v.revenuePerSession || 0)}</TableCell>
                  <TableCell align='right'>
                    {v.isControl || v.confidence === null ? '—' : fmtPct(v.confidence)}
                  </TableCell>
                  <TableCell>
                    <Chip size='small' label={vd.label} color={vd.color === 'default' ? undefined : vd.color} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
        Signifikanz: zweiseitiger Zwei-Proportionen-z-Test je Variante gegen Control. „Signifikant"
        ab {Math.round(CONFIDENCE_THRESHOLD * 100)} % Konfidenz und mind. {MIN_SESSIONS_FOR_VERDICT}{' '}
        Sessions pro Arm. €/Session berücksichtigt auch den Warenkorbwert, nicht nur die Rate.
      </Typography>
    </Box>
  );
}

function ExperimentsView() {
  const authCtx = useAuth();
  const { experiments, isLoading, fetchExperiments, updateExperiment, fetchResults } =
    useExperiments();

  const [weightsDraft, setWeightsDraft] = useState({});
  const [openResults, setOpenResults] = useState({});
  const [results, setResults] = useState({}); // key -> { loading, data }
  const [startDate, setStartDate] = useState(dayjs().subtract(14, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

  const canRead = authCtx.checkRight(READ_RIGHT);
  const canManage = authCtx.checkRight(MANAGE_RIGHT);

  useEffect(() => {
    if (!canRead) return;
    fetchExperiments().catch(() => {});
  }, [canRead, fetchExperiments]);

  // Seed the editable weights from the loaded experiments (without clobbering edits).
  useEffect(() => {
    setWeightsDraft(prev => {
      const next = { ...prev };
      for (const exp of experiments) {
        if (!next[exp.key]) {
          next[exp.key] = Object.fromEntries(exp.variants.map(v => [v.key, v.weight]));
        }
      }
      return next;
    });
  }, [experiments]);

  const loadResults = useMemo(
    () => async key => {
      setResults(prev => ({ ...prev, [key]: { loading: true, data: prev[key]?.data } }));
      try {
        const data = await fetchResults(key, { startDate, endDate });
        setResults(prev => ({ ...prev, [key]: { loading: false, data } }));
      } catch {
        setResults(prev => ({ ...prev, [key]: { loading: false, data: prev[key]?.data } }));
      }
    },
    [fetchResults, startDate, endDate]
  );

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  const handleToggleEnabled = exp => {
    updateExperiment(exp.key, { enabled: !exp.enabled }).catch(() => {});
  };

  const handleWeightChange = (key, variantKey, value) => {
    const n = Math.max(0, Math.round(Number(value) || 0));
    setWeightsDraft(prev => ({ ...prev, [key]: { ...prev[key], [variantKey]: n } }));
  };

  const handleSaveWeights = exp => {
    updateExperiment(exp.key, { weights: weightsDraft[exp.key] || {} })
      .then(updated => {
        if (updated) {
          setWeightsDraft(prev => ({
            ...prev,
            [exp.key]: Object.fromEntries(updated.variants.map(v => [v.key, v.weight])),
          }));
        }
      })
      .catch(() => {});
  };

  const handleEqualSplit = exp => {
    setWeightsDraft(prev => ({
      ...prev,
      [exp.key]: Object.fromEntries(exp.variants.map(v => [v.key, 1])),
    }));
  };

  const handleToggleResults = exp => {
    const willOpen = !openResults[exp.key];
    setOpenResults(prev => ({ ...prev, [exp.key]: willOpen }));
    if (willOpen && !results[exp.key]?.data) loadResults(exp.key);
  };

  const reloadOpenResults = () => {
    Object.keys(openResults).forEach(key => {
      if (openResults[key]) loadResults(key);
    });
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1180, mx: 'auto' }}>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1 }}>
        <ScienceOutlinedIcon color='primary' />
        <Typography variant='h4' sx={{ fontWeight: 700 }}>
          A/B-Tests
        </Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Übersicht aller Experimente im Booking-Widget. Schalte jeden Test ein/aus, verteile den
        Traffic frei auf beliebig viele Varianten und werte pro Variante Conversion, Umsatz/Session,
        Uplift und statistische Signifikanz aus. Jedes Experiment ist standardmäßig AUS — solange es
        aus ist, sieht jeder Besucher die aktuelle Strecke (Control). Änderungen wirken sofort.
      </Typography>

      {!canManage && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Nur-Lese-Ansicht — zum Schalten/Verteilen fehlt das Recht <code>{MANAGE_RIGHT}</code>.
        </Alert>
      )}

      {/* Shared results date range */}
      <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            Auswertungs-Zeitraum
          </Typography>
          <TextField
            type='date'
            label='Von'
            size='small'
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type='date'
            label='Bis'
            size='small'
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant='outlined' size='small' onClick={reloadOpenResults}>
            Ergebnisse aktualisieren
          </Button>
        </Stack>
      </Paper>

      {isLoading && experiments.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && experiments.length === 0 && (
        <Alert severity='info'>Aktuell sind keine Experimente definiert.</Alert>
      )}

      <Stack spacing={2}>
        {experiments.map(exp => {
          const draft = weightsDraft[exp.key] || {};
          const totalWeight = Object.values(draft).reduce((s, w) => s + (Number(w) || 0), 0);
          const res = results[exp.key];
          return (
            <Paper
              key={exp.key}
              variant='outlined'
              sx={{
                p: 2,
                borderColor: exp.enabled ? 'success.main' : 'divider',
                borderWidth: exp.enabled ? 2 : 1,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ sm: 'center' }}
                spacing={1}
              >
                <Box>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography variant='h6' sx={{ fontWeight: 700 }}>
                      {exp.name}
                    </Typography>
                    <Chip
                      size='small'
                      label={exp.enabled ? 'LIVE' : 'Aus'}
                      color={exp.enabled ? 'success' : 'default'}
                    />
                  </Stack>
                  <Typography variant='caption' color='text.secondary'>
                    {exp.key}
                    {exp.surface ? ` · ${exp.surface}` : ''}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(exp.enabled)}
                      onChange={() => handleToggleEnabled(exp)}
                      disabled={!canManage}
                    />
                  }
                  label={exp.enabled ? 'Aktiv' : 'Inaktiv'}
                  labelPlacement='start'
                />
              </Stack>

              <Typography variant='body2' sx={{ mt: 1 }}>
                {exp.description}
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              {/* Variant weights editor */}
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Varianten & Traffic-Verteilung
              </Typography>
              {!exp.enabled && (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                  Test ist aus — schalte ihn ein, damit der Traffic verteilt wird.
                </Typography>
              )}
              <Stack spacing={1}>
                {exp.variants.map(v => {
                  const w = Number(draft[v.key] ?? v.weight ?? 0);
                  const share = totalWeight > 0 ? Math.round((w / totalWeight) * 100) : 0;
                  return (
                    <Stack
                      key={v.key}
                      direction='row'
                      spacing={2}
                      alignItems='center'
                      sx={{ flexWrap: 'wrap' }}
                    >
                      <Box sx={{ minWidth: 220, flex: 1 }}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {v.name}
                          </Typography>
                          {v.isControl && <Chip size='small' variant='outlined' label='Control' />}
                        </Stack>
                        <Typography variant='caption' color='text.secondary'>
                          {v.description}
                        </Typography>
                      </Box>
                      <TextField
                        type='number'
                        label='Gewicht'
                        size='small'
                        value={Number.isFinite(w) ? w : 0}
                        onChange={e => handleWeightChange(exp.key, v.key, e.target.value)}
                        disabled={!canManage || !exp.enabled}
                        inputProps={{ min: 0, step: 1, style: { width: 70 } }}
                      />
                      <Box sx={{ minWidth: 140 }}>
                        <Typography variant='caption' color='text.secondary'>
                          {exp.enabled ? `${share} % des Traffics` : '—'}
                        </Typography>
                        <LinearProgress
                          variant='determinate'
                          value={exp.enabled ? share : 0}
                          sx={{ height: 6, borderRadius: 3, mt: 0.25 }}
                        />
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
              {canManage && (
                <Stack direction='row' spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    variant='contained'
                    size='small'
                    onClick={() => handleSaveWeights(exp)}
                    disabled={!exp.enabled}
                  >
                    Verteilung speichern
                  </Button>
                  <Button
                    variant='text'
                    size='small'
                    onClick={() => handleEqualSplit(exp)}
                    disabled={!exp.enabled}
                  >
                    Gleichverteilt
                  </Button>
                </Stack>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Results */}
              <Button
                variant='outlined'
                size='small'
                startIcon={<QueryStatsIcon />}
                onClick={() => handleToggleResults(exp)}
              >
                {openResults[exp.key] ? 'Ergebnisse ausblenden' : 'Ergebnisse anzeigen'}
              </Button>
              <Collapse in={Boolean(openResults[exp.key])} unmountOnExit>
                {res?.loading && !res?.data ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <ResultsPanel result={res?.data} />
                )}
              </Collapse>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

export default ExperimentsView;
